import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion, Alert, Badge, Button, CodeField, DataTable, DefinitionList, Dialog, EmptyState, FormSection, Link, SplitLayout, Stack, t, Tabs, Text, toast,
} from '@kanbios/ui';
import type { provisioning } from '@kanbios/api-client';
import { RefusalPage } from '../components/Refusal';
import { PullRequest, PullRequestNotice, changeRef } from '../components/catalogue/PullRequestNotice';
import { AttachmentDraft, AttachmentTable } from '../components/enrolment/Attachments';
import { ManualEntry } from '../components/enrolment/ManualEntry';
import { EnrolmentProgress, failedServers, useEnrolmentJob } from '../components/enrolment/EnrolmentProgress';
import { firewallFrom, PortAccess, portCounts, PortsReview } from '../components/enrolment/PortsReview';
import { api, ApiFailure, causeOf, unwrap } from '../lib/api';
import { relativeTime } from '../lib/format';
import { FULL_HEIGHT } from '../lib/tables';
import { PendingButton, pendingLabel } from '../components/PendingButton';
import { openPullRequestOf, PullRequestConflict } from '../components/PullRequestConflict';
import { AccessCheckStatus, useAccessCheck } from '../components/AccessCheck';
import { ACCESS_TONES, AccessDetailDrawer, accessSummary } from '../components/enrolment/AccessDetail';

type Candidate = provisioning.components['schemas']['DiscoveredServer'];
/** SPEC-007 RG-007-10. */
const MAX_SERVERS = 20;
// D69: Accès and Pare-feu are two steps (the firewall is reviewed once every server trusts the platform).
const STEPS = ['source', 'attachment', 'access', 'firewall', 'enrolment'] as const;
const LAST = STEPS.length - 1;
const DRAFT_KEY = 'kanbios-import-draft';

interface Draft { selected: string[]; decisions: Record<string, PortAccess>; step: number }

const readDraft = (): Draft | null => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
};
const writeDraft = (draft: Draft | null) => {
  try {
    if (draft) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    else sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* private window: the draft only lives in the page */
  }
};
/** « a, b » or « a, b +3 », for titles and the summary. */
const names = (list: string[], max = 2) => (list.length <= max ? list.join(', ') : `${list.slice(0, max).join(', ')} +${list.length - max}`);

/**
 * « Importer un serveur existant » (SPEC-007), DSM Split page type with steps
 * (D66: the form takes 2/3 of the width, the summary the rest): five steps (D69),
 * the last one a verification, then the « et maintenant ? » screen that
 * follows the enrolment. Brought forward to sprint 1 by the sponsor
 * (17 October 2026) without Rattachement and manual entry, which come with the
 * sprint 2 catalogue (pull requests, ADR-005).
 */
export function ImportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  // A retry or a return from the strong authentication brings the selection back.
  const initial = useMemo<Draft>(() => {
    const fromUrl = params.get('servers')?.split(',').filter(Boolean);
    // ?step=attachment: from a server page, « Modifier le rattachement » (D23).
    if (fromUrl?.length) return { selected: fromUrl, decisions: {}, step: params.get('step') === 'attachment' ? 1 : 2 };
    return readDraft() ?? { selected: [], decisions: {}, step: 0 };
  }, [params]);
  const [step, setStep] = useState(initial.step);
  const [selected, setSelected] = useState<string[]>(initial.selected);
  const [decisions, setDecisions] = useState<Record<string, PortAccess>>(initial.decisions);
  const [jobId, setJobId] = useState<string | null>(null);
  const [confirmProduction, setConfirmProduction] = useState(false);
  const [quit, setQuit] = useState(false);
  const [stepUp, setStepUp] = useState(false);
  const [source, setSource] = useState<'discovery' | 'manual'>('discovery');
  const [attachments, setAttachments] = useState<Record<string, AttachmentDraft>>({});
  const [attachmentPr, setAttachmentPr] = useState<PullRequest | null>(null);
  const [attachmentTried, setAttachmentTried] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [attachFailure, setAttachFailure] = useState<ApiFailure | null>(null);
  // D59: the server whose access detail is open (DSM Drawer).
  const [accessDetail, setAccessDetail] = useState<string | null>(null);
  useEffect(() => { if (!jobId) writeDraft({ selected, decisions, step }); }, [selected, decisions, step, jobId]);

  // The servers the wizard opens with (URL, or the draft restored after the strong authentication)
  // come back even when enrolled: « Réappliquer la configuration » (SPEC-032).
  const reapplied = initial.selected.join(',');
  const discovered = useQuery({
    queryKey: ['discovered-servers', reapplied],
    queryFn: async () => unwrap(await api.provisioning.GET('/discovered-servers', { params: { query: reapplied ? { servers: reapplied } : {} } })),
    refetchInterval: query => (query.state.data?.checking ? 3_000 : 15_000),
  });
  const candidates = (discovered.data?.items ?? []).filter(c => c.enrolment?.state !== 'running' && c.access !== 'not_applicable');
  const chosen = candidates.filter(c => selected.includes(c.server));
  const unavailable = selected.filter(n => !candidates.some(c => c.server === n));
  const notTrusted = chosen.filter(c => c.access !== 'trusted');
  const trusted = chosen.filter(c => c.access === 'trusted');
  // Ports and the firewall already there (ufw) are read by the same check: both are needed.
  const unread = trusted.filter(c => !c.listening || !c.hostFirewall || c.hostFirewall.ufw === 'unreadable');
  const withUfw = trusted.filter(c => c.hostFirewall?.ufw === 'active');
  const production = chosen.filter(c => (attachments[c.server]?.environment ?? c.environment) === 'prod').map(c => c.server);
  const ready = chosen.length > 0 && notTrusted.length === 0 && unread.length === 0;
  const blockedFrom = discovered.data?.blockedFrom ?? null;
  // ufw there but its answer not understood: shown as it answered, never guessed.
  const unreadable = trusted.filter(c => c.hostFirewall?.ufw === 'unreadable');
  // Rattachement (SPEC-007): each server's draft, from what the catalogue says of it.
  const rows: AttachmentDraft[] = chosen.map(c => attachments[c.server] ?? { server: c.server, environment: c.environment, team: c.team, criticality: c.criticality, deployment: null });
  const importOptions = useQuery({
    queryKey: ['server-import-options'],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/imports/options')),
    enabled: step === 1 || source === 'manual',
  });
  const attachmentBody = { attachments: rows };
  const attachmentPreview = useQuery({
    queryKey: ['server-import-preview', attachmentBody],
    queryFn: async () => unwrap(await api.maintenance.POST('/servers/imports/preview', { body: attachmentBody })),
    enabled: step === 1 && rows.length > 0 && !attachmentPr && importOptions.data?.writable === true,
    placeholderData: keepPreviousData,
  });
  // Catalogue writes need the GitHub App (sponsor actions 8 to 10): until then, attachments are shown, not changed.
  const writable = importOptions.data?.writable ?? true;
  const attachmentErrors = writable ? attachmentPreview.data?.errors ?? [] : [];
  const attachmentChanges = attachmentPreview.data?.pullRequests ?? 0;
  // From a server page, an enrolled server only changes its attachment: no enrolment after it.
  const attachmentOnly = params.get('step') === 'attachment' && chosen.length > 0 && chosen.every(c => c.enrolment?.state === 'enrolled');
  /** « Continuer » from Rattachement opens its pull request when something changed (one for all the servers). */
  const leaveAttachment = async () => {
    if (!writable) { setStep(2); return; }
    setAttachmentTried(true);
    // A click while the preview reloads (a value just changed, a pull request just closed) waits for it
    // instead of being lost.
    const preview = attachmentPreview.isFetching ? (await attachmentPreview.refetch()).data : attachmentPreview.data;
    if (preview?.errors?.length) return;
    let opened = attachmentPr;
    if ((preview?.pullRequests ?? 0) > 0 && !attachmentPr) {
      setAttaching(true);
      setAttachFailure(null);
      try {
        opened = unwrap(await api.maintenance.POST('/servers/imports', { body: attachmentBody })).pullRequest;
        setAttachmentPr(opened);
      } catch (e) {
        setAttachFailure(e instanceof ApiFailure ? e : null);
        return;
      } finally {
        setAttaching(false);
      }
    }
    // « Modifier le rattachement » of an enrolled server (D23): the attachment is all that was asked.
    if (attachmentOnly) {
      if (opened) toast({ tone: 'pos', title: t('import.attachmentOnly.opened', { ref: changeRef(opened) }) });
      writeDraft(null);
      navigate(`/servers/${encodeURIComponent(chosen[0]!.server)}`);
      return;
    }
    setStep(2);
  };
  // A re-enrolment starts from the firewall decided last time (SPEC-032): open ports keep their
  // opening, the others start closed; the person still reviews each one.
  useEffect(() => {
    const fill: Record<string, PortAccess> = {};
    for (const c of chosen) {
      const plan = c.lastFirewall;
      if (!plan || !c.listening) continue;
      for (const l of c.listening) {
        const key = `${c.server}/${l.proto}/${l.port}`;
        if (key in decisions) continue;
        const open = (list: number[]) => list.includes(l.port);
        fill[key] = l.proto === 'tcp'
          ? (open(plan.publicTcp) ? 'public' : open(plan.platformTcp) ? 'platform' : 'closed')
          : (open(plan.publicUdp) ? 'public' : open(plan.platformUdp) ? 'platform' : 'closed');
      }
    }
    if (Object.keys(fill).length) setDecisions(d => ({ ...fill, ...d }));
  }, [discovered.data]);
  const environmentOf = (c: Candidate) => attachments[c.server]?.environment ?? c.environment;
  const bootstrap = useQuery({
    queryKey: ['ssh-bootstrap'],
    enabled: step === 2 && notTrusted.length > 0,
    queryFn: async () => unwrap(await api.serverAccess.GET('/bootstrap')),
  });
  // D59: a check that starts after the click, of the servers concerned only (fast), followed to its
  // result; the rows update in place when it ends.
  const access = useAccessCheck();
  const runCheck = (servers: string[]) => access.run(servers);
  const enrol = useMutation({
    mutationFn: async (confirm: boolean) => unwrap(await api.provisioning.POST('/enrolments', {
      body: { servers: chosen.map(c => c.server), confirmProduction: confirm, firewall: firewallFrom(chosen, decisions) },
    })),
    onSuccess: started => {
      writeDraft(null);
      setJobId(started.jobId);
      void queryClient.invalidateQueries({ queryKey: ['enrolments'] });
    },
    onError: error => {
      // Enrolling changes servers: a recent strong authentication (CDC § 7.2).
      if (error instanceof ApiFailure && error.body.code === 'MFA_STEP_UP_REQUIRED') setStepUp(true);
      else if (error instanceof ApiFailure) toast({ title: error.body.message, description: error.body.hint, tone: 'neg', duration: 10_000 });
    },
  });
  const job = useEnrolmentJob(jobId);

  if (discovered.error) return <RefusalPage error={discovered.error} />;

  const leave = () => { writeDraft(null); navigate('/pending-servers'); };
  const close = () => (jobId || selected.length === 0 ? leave() : setQuit(true));
  const restart = (servers: string[]) => {
    setSelected(servers);
    setDecisions({});
    setJobId(null);
    setStep(servers.length ? 2 : 0);
  };
  const counts = portCounts(trusted, decisions);
  const canContinue = step === 0 ? source === 'discovery' && chosen.length > 0 && chosen.length <= MAX_SERVERS : step === 1 ? !attaching : ready;
  // D69: a server that cannot be bootstrapped now leaves the selection, for a later enrolment.
  const remove = (servers: string[]) => setSelected(list => list.filter(name => !servers.includes(name)));
  const steps = STEPS.map(s => t(`import.steps.${s}`));
  const accessBadge = (c: Candidate) => <Badge tone={ACCESS_TONES[c.access]}>{t(`settings.access.states.${c.access}`)}</Badge>;
  const todo = (text: string) => <Text variant="body-sm" tone="mute">{text}</Text>;
  const copySnippet = async (snippet: string) => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast({ tone: 'pos', title: t('import.access.snippetCopied') });
    } catch {
      toast({ tone: 'neg', title: t('import.access.snippetNotCopied') });
    }
  };
  const checkAction = access.running ? t('settings.access.checking') : t('import.access.check');

  // --- « Et maintenant ? »: the enrolment is launched (the same frame, its last step, no summary).
  if (jobId) {
    const data = job.data;
    const failed = failedServers(data);
    const total = data?.servers.length ?? chosen.length;
    const outcome = !data || data.status === 'running' || data.status === 'queued' ? 'running' : data.status;
    return (
      <SplitLayout
        title={t('import.title')}
        steps={steps}
        current={LAST}
        onBack={leave}
        backLabel={t('import.quit')}
        heading={t(`import.done.heading.${outcome}`, { n: total - failed.length, total })}
        lead={t(`import.done.lead.${outcome}`)}
        footerStart={failed.length > 0 && outcome !== 'running'
          ? <Button variant="secondary" icon="replay" onClick={() => restart(failed)}>{t('import.done.retry', { n: failed.length })}</Button>
          : undefined}
        footerEnd={<Button icon="arrow_forward" onClick={leave}>{t('import.seeList')}</Button>}
        footerText=""
      >
        <Stack gap={24}>
          <EnrolmentProgress job={data} loading={job.isPending} height={FULL_HEIGHT} results={false} />
          <Stack gap={8}>
            <Link href="/servers/import" onClick={(e: { preventDefault(): void }) => { e.preventDefault(); restart([]); }}>{t('import.done.links.again')}</Link>
          </Stack>
        </Stack>
      </SplitLayout>
    );
  }

  return (
    <>
      <SplitLayout
        title={t('import.title')}
        subtitle={t('import.subtitle')}
        steps={steps}
        current={step}
        onStepSelect={i => { if (i < step) setStep(i); }}
        onBack={close}
        backLabel={t('import.quit')}
        heading={t(`import.${STEPS[step]}.heading`)}
        lead={t(`import.${STEPS[step]}.lead`)}
        // D66: the tables get 2/3 of the width; the summary, plain content on the surface pane (no LiveSummary card).
        split="2-1"
        asideLabel={t('import.summary.eyebrow')}
        aside={
          <Stack gap={16}>
            <Text variant="section-title" as="h2">{chosen.length ? t('import.summary.title', { n: chosen.length }) : t('import.summary.titleEmpty')}</Text>
            <DefinitionList
              stacked
              items={[
                { key: 'servers', label: t('import.summary.labels.servers'), value: chosen.length ? names(chosen.map(c => c.server)) : todo(t('import.summary.serversTodo')) },
                {
                  key: 'attachment', label: t('import.summary.labels.attachment'),
                  value: step >= 2 ? (attachmentPr ? t('import.summary.attachmentPr', { ref: changeRef(attachmentPr) }) : writable ? t('import.summary.attachmentSame') : t('import.summary.attachmentLater')) : todo(t('import.summary.attachmentTodo')),
                },
                { key: 'access', label: t('import.summary.labels.access'), value: chosen.length && step >= 2 ? t('import.summary.access', { n: trusted.length, total: chosen.length }) : todo(t('import.summary.accessTodo')) },
                { key: 'ports', label: t('import.summary.labels.ports'), value: ready && step >= 3 ? t('import.summary.portsShort', counts) : todo(t('import.summary.portsTodo')) },
                {
                  key: 'production', label: t('import.summary.labels.production'),
                  value: chosen.length ? (production.length ? t('import.summary.production', { n: production.length }) : t('import.summary.noProduction')) : todo(t('import.summary.productionTodo')),
                },
              ]}
            />
            <Text variant="body-sm" tone="mute">{step === LAST && ready ? t('import.summary.ready') : t('import.summary.tasks')}</Text>
          </Stack>
        }
        footerStart={<Button variant="secondary" icon="arrow_back" disabled={step === 0} onClick={() => setStep(step - 1)}>{t('import.previous')}</Button>}
        footerEnd={step < LAST
          ? <PendingButton trailingIcon="arrow_forward" pending={step === 1 && attaching} disabled={!canContinue} onClick={() => (step === 1 ? void leaveAttachment() : setStep(step + 1))}>{step === 1 && attachmentOnly ? t(attachmentChanges > 0 ? 'import.attachmentOnly.save' : 'import.attachmentOnly.done') : t('import.continue')}</PendingButton>
          : (
            <PendingButton icon="rocket_launch" pending={enrol.isPending} disabled={!ready}
              onClick={() => (production.length ? setConfirmProduction(true) : enrol.mutate(false))}>
              {t('import.launch', { n: chosen.length })}
            </PendingButton>
          )}
      >
        {step === 0 && (
          <Tabs
            label={t('import.source.tabs')}
            value={source}
            onChange={id => setSource(id as 'discovery' | 'manual')}
            items={[{ id: 'discovery', label: t('import.source.discovery') }, { id: 'manual', label: t('import.source.manual') }]}
          />
        )}
        {step === 0 && source === 'manual' && <ManualEntry options={importOptions.data} />}
        {step === 0 && source === 'discovery' && (
          <Stack gap={16}>
            {discovered.data?.configured === false && (
              <Alert tone="warn" title={t('import.source.notConfiguredTitle')}>{t('settings.enrolment.notConfigured')}</Alert>
            )}
            {chosen.length > MAX_SERVERS && (
              <Alert tone="warn" title={t('import.source.tooManyTitle', { n: chosen.length, max: MAX_SERVERS })}>{t('import.source.tooManyBody')}</Alert>
            )}
            <DataTable
              label={t('import.source.table')}
              rows={candidates}
              rowKey="server"
              loading={discovered.isPending}
              selectable
              selected={Object.fromEntries(selected.map(s => [s, true as const]))}
              onSelectionChange={sel => setSelected(Object.keys(sel))}
              density="compact"
              height={FULL_HEIGHT}
              searchPlaceholder={t('import.source.search')}
              toolbarDensity={false}
              toolbarColumns={false}
              columns={[
                // The server, its access, then one fact per column, named as in the servers list.
                { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 200, pinned: true },
                { key: 'access', label: t('settings.access.columns.state'), width: 160, filter: true, accessor: (c: Candidate) => t(`settings.access.states.${c.access}`), render: accessBadge },
                { key: 'environment', label: t('import.source.environment'), width: 140, filter: true },
                { key: 'provider', label: t('servers.columns.provider'), width: 130, accessor: (c: Candidate) => c.provider || '—' },
                { key: 'region', label: t('servers.columns.region'), width: 110, accessor: (c: Candidate) => c.region || '—' },
                { key: 'ip', label: t('servers.columns.ip'), mono: true, width: 140, accessor: (c: Candidate) => c.ip ?? '—' },
                { key: 'os', label: t('import.source.os'), grow: true, minWidth: 120, render: (c: Candidate) => c.os ?? '—' },
              ]}
              empty={<EmptyState icon="task_alt" title={t('import.source.emptyTitle')} framed={false}>{t('import.source.emptyBody')}</EmptyState>}
            />
          </Stack>
        )}

        {/* A server named in the URL that cannot be enrolled here (archived, out of scope, enrolment running). */}
        {step > 0 && discovered.data && unavailable.length > 0 && (
          <Alert tone="warn" title={t('import.unavailableTitle', { servers: names(unavailable, 3) })}>{t('import.unavailableBody')}</Alert>
        )}
        {step === 1 && (
          <Stack gap={16}>
            {attachFailure && openPullRequestOf(attachFailure) ? (
              // The attachment pull request still open: see it, or close it here and « Continuer » again.
              <PullRequestConflict failure={attachFailure} onClosed={() => setAttachFailure(null)} />
            ) : attachFailure && <Alert tone="neg" title={attachFailure.body.message}>{attachFailure.body.hint}{causeOf(attachFailure) ? ` ${t('errors.cause', { cause: causeOf(attachFailure)! })}` : ''}</Alert>}
            {!writable ? (
              <Alert tone="info" title={t('import.attachment.notWritableTitle')}>{t('import.attachment.notWritableBody')}</Alert>
            ) : attachmentPr ? (
              <PullRequestNotice pullRequest={attachmentPr} />
            ) : (
              <Alert tone="info" title={attachmentChanges ? t('import.attachment.changesTitle') : t('import.attachment.sameTitle')}>{t('import.attachment.body')}</Alert>
            )}
            {attachmentTried && attachmentErrors.length > 0 && (
              <Alert tone="neg" title={t('import.attachment.errorsTitle', { n: new Set(attachmentErrors.map(e => e.server)).size })}>
                {[...new Set(attachmentErrors.map(e => `${e.server} : ${e.message}`))].join(' ')}
              </Alert>
            )}
            <AttachmentTable
              rows={rows}
              onChange={v => setAttachments(a => ({ ...a, [v.server]: v }))}
              options={importOptions.data}
              errors={attachmentTried ? attachmentErrors : attachmentErrors.filter(e => e.field === 'deployment')}
              warnings={writable ? attachmentPreview.data?.warnings ?? [] : []}
              disabled={!writable || Boolean(attachmentPr)}
            />
          </Stack>
        )}

        {step === 2 && (
          // On the page ground, divided by their hairline (SplitLayout: the pane is the container).
          <Stack gap={16}>
            <FormSection layout="stacked" headingLevel={3} title={t('import.access.title')} description={t('import.access.description')}>
              <Stack gap={16}>
                {/* Checks are skipped from an address the servers refuse: say it, the button would seem dead. */}
                {blockedFrom && (
                  <Alert tone="neg" title={t('import.access.egressTitle', { ip: blockedFrom })}>{t('import.access.egressBody')}</Alert>
                )}
                {unreadable.map(c => (
                  <Alert key={c.server} tone="neg" title={t('import.access.ufwUnreadableTitle', { server: c.server })} action={checkAction} onAction={() => runCheck([c.server])}>
                    <Stack gap={8}>
                      <Text variant="body-sm">{t('import.access.ufwUnreadableBody')}</Text>
                      {c.hostFirewall!.rules.length > 0 ? (
                        <CodeField label={t('import.access.ufwAnswer', { server: c.server })} readOnly lineNumbers={false} copyable={false}
                          rows={Math.min(5, c.hostFirewall!.rules.length)} value={c.hostFirewall!.rules.join('\n')} />
                      ) : (
                        <Text variant="body-sm">{t('import.access.ufwNoAnswer')}</Text>
                      )}
                    </Stack>
                  </Alert>
                ))}
                {notTrusted.length > 0 ? (
                  <>
                    {/* The servers not trusted yet, and only them: the check right after the bootstrap. */}
                    <Alert tone="warn" title={t('import.access.blockedTitle', { n: notTrusted.length, servers: names(notTrusted.map(c => c.server), 3) })}
                      action={checkAction} onAction={() => runCheck(notTrusted.map(c => c.server))}>
                      {t('import.access.blockedBody')}
                    </Alert>
                    {/* D66: the command is copied from here; it opens only to be read. */}
                    <Stack gap={8} align="start">
                      <Button variant="secondary" icon="content_copy" disabled={!bootstrap.data?.snippet} onClick={() => void copySnippet(bootstrap.data?.snippet ?? '')}>
                        {t('import.access.copySnippet')}
                      </Button>
                      <Text variant="body-sm" tone="mute">{t('import.access.hint')}</Text>
                    </Stack>
                    {/* D69: not bootstrappable now: out of this enrolment, the others go on. */}
                    {notTrusted.length < chosen.length && (
                      <Stack gap={8} align="start">
                        <Button variant="secondary" icon="playlist_remove" onClick={() => remove(notTrusted.map(c => c.server))}>
                          {t('import.access.removeBlocked', { n: notTrusted.length })}
                        </Button>
                      </Stack>
                    )}
                    <Accordion headingLevel={4} items={[{
                      id: 'snippet', title: t('import.access.showSnippet'),
                      content: <CodeField label={t('settings.access.bootstrap.label')} language="bash" lineNumbers readOnly rows={8} maxHeight={320} value={bootstrap.data?.snippet ?? ''} />,
                    }]} />
                  </>
                ) : unread.length > unreadable.length ? (
                  <Alert tone="warn" title={t('settings.enrolment.review.unreadTitle', { n: unread.length })} action={checkAction} onAction={() => runCheck(unread.map(c => c.server))}>
                    {t('import.access.unreadBody', { servers: names(unread.filter(c => !unreadable.includes(c)).map(c => c.server), 3) })}
                  </Alert>
                ) : unreadable.length > 0 || chosen.length === 0 ? null : (
                  <Alert tone="pos" title={t('import.access.readyTitle', { n: chosen.length })} />
                )}
                {/* The check asked here: its progress, then its result (the rows update in place). */}
                <AccessCheckStatus feedback={access.feedback} />
                <DataTable
                  label={t('import.access.table')}
                  rows={chosen}
                  rowKey="server"
                  toolbar={false}
                  density="compact"
                  height={FULL_HEIGHT}
                  // D59: a row opens what its last check tested and met.
                  onRowClick={(c: Candidate) => setAccessDetail(c.server)}
                  activeRowKey={accessDetail ?? undefined}
                  columns={[
                    { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 200 },
                    // D66: the address the check reaches.
                    { key: 'ip', label: t('servers.columns.ip'), mono: true, width: 140, accessor: (c: Candidate) => c.ip ?? '—' },
                    { key: 'access', label: t('settings.access.columns.state'), width: 160, render: accessBadge },
                    // When the last check ran: a click on « Vérifier l'accès » shows here.
                    { key: 'checkedAt', label: t('settings.access.columns.checked'), width: 140, render: (c: Candidate) => (c.checkedAt ? relativeTime(c.checkedAt) : '—') },
                    // What the check met, in a few words; the whole of it in the drawer.
                    { key: 'detail', label: t('settings.access.columns.detail'), grow: true, accessor: (c: Candidate) => accessSummary(c) },
                    {
                      key: 'check', label: t('import.access.columns.check'), width: 140, pinned: 'right' as const, sortable: false,
                      render: (c: Candidate) => (
                        <PendingButton size="sm" variant="secondary" icon="refresh" pending={access.checking(c.server)}
                          aria-label={t('import.access.checkOne', { server: c.server })} onClick={() => runCheck([c.server])}>
                          {t('import.access.checkShort')}
                        </PendingButton>
                      ),
                    },
                    {
                      // D69: a server leaves the selection here (the last one stays: « Précédent » goes back to Source).
                      key: 'remove', label: t('import.access.columns.remove'), width: 120, pinned: 'right' as const, sortable: false,
                      render: (c: Candidate) => (chosen.length > 1 ? (
                        <Button size="sm" variant="ghost" icon="close" aria-label={t('import.access.removeOne', { server: c.server })} onClick={() => remove([c.server])}>
                          {t('import.access.removeShort')}
                        </Button>
                      ) : null),
                    },
                  ]}
                />
                <Text variant="body-sm" tone="mute">{t('import.access.openDetail')}</Text>
                <AccessDetailDrawer
                  server={chosen.find(c => c.server === accessDetail) ?? null}
                  onClose={() => setAccessDetail(null)}
                  onCheck={server => runCheck([server])}
                  checking={Boolean(accessDetail && access.checking(accessDetail))}
                />
              </Stack>
            </FormSection>
          </Stack>
        )}

        {step === 3 && (
          <Stack gap={16}>
            <FormSection layout="stacked" headingLevel={3} title={t('import.firewall.title')} description={t('import.firewall.description')}>
              {trusted.length > unread.length ? (
                <Stack gap={16}>
                  {/* ufw already there: its rules, read before the platform's firewall replaces it. */}
                  {withUfw.map(c => (
                    <Alert key={c.server} tone="warn" title={t('import.firewall.ufwTitle', { server: c.server })}>
                      <Stack gap={8}>
                        <Text variant="body-sm">{t('import.firewall.ufwBody')}</Text>
                        {c.hostFirewall!.rules.length > 0
                          ? <CodeField label={t('import.firewall.ufwRules', { server: c.server })} readOnly lineNumbers={false} copyable={false} rows={Math.min(8, c.hostFirewall!.rules.length)} value={c.hostFirewall!.rules.join('\n')} />
                          : <Text variant="body-sm">{t('import.firewall.ufwNoRules')}</Text>}
                      </Stack>
                    </Alert>
                  ))}
                  <PortsReview servers={trusted.filter(c => c.listening && c.hostFirewall)} decisions={decisions} onChange={(key, access) => setDecisions(d => ({ ...d, [key]: access }))} />
                </Stack>
              ) : <Text variant="body-sm" tone="mute">{t('import.firewall.waiting')}</Text>}
            </FormSection>
          </Stack>
        )}

        {step === LAST && (
          <Stack gap={16}>
            {/* « Enrôler » stays off until each server trusts the platform and its ports are read: say which, and where. */}
            {notTrusted.length > 0 && (
              <Alert tone="neg" title={t('import.enrolment.notTrustedTitle', { n: notTrusted.length, servers: names(notTrusted.map(c => c.server), 3) })}
                action={t('import.enrolment.toAccess')} onAction={() => setStep(STEPS.indexOf('access'))}>
                {t('import.enrolment.notTrustedBody')}
              </Alert>
            )}
            {unread.length > 0 && (
              <Alert tone="warn" title={t('import.enrolment.unreadTitle', { n: unread.length, servers: names(unread.map(c => c.server), 3) })}
                action={t('import.enrolment.toFirewall')} onAction={() => setStep(STEPS.indexOf('firewall'))}>
                {t('import.enrolment.unreadBody')}
              </Alert>
            )}
            {production.length > 0 && (
              <Alert tone="warn" title={t('import.enrolment.productionTitle', { n: production.length, servers: names(production, 3) })}>
                {t('import.enrolment.productionBody')}
              </Alert>
            )}
            {withUfw.length > 0 && (
              <Alert tone="warn" title={t('import.enrolment.ufwTitle', { n: withUfw.length, servers: names(withUfw.map(c => c.server), 3) })}>
                {t('import.enrolment.ufwBody')}
              </Alert>
            )}
            <FormSection layout="stacked" headingLevel={3} title={t('import.enrolment.serversTitle', { n: chosen.length })}>
              <DataTable
                label={t('import.enrolment.table')}
                rows={chosen}
                rowKey="server"
                toolbar={false}
                density="compact"
                height={FULL_HEIGHT}
                columns={[
                  { key: 'server', label: t('settings.access.columns.server'), mono: true, width: 200 },
                  { key: 'environment', label: t('import.source.environment'), width: 140, render: (c: Candidate) => environmentOf(c) },
                  { key: 'ports', label: t('import.enrolment.ports'), grow: true, render: (c: Candidate) => t('import.summary.ports', portCounts([c], decisions)) },
                ]}
              />
            </FormSection>
            <FormSection layout="stacked" headingLevel={3} title={t('import.enrolment.appliedTitle')}>
              <DefinitionList
                items={(['base', 'firewall', 'agent', 'inventory', 'alerts'] as const).map(key => ({
                  key, label: t(`import.enrolment.applied.${key}`), value: t(`import.enrolment.applied.${key}Value`),
                }))}
              />
            </FormSection>
            <Alert tone="info" title={t('import.enrolment.replayTitle')}>{t('import.enrolment.replayBody')}</Alert>
          </Stack>
        )}
      </SplitLayout>

      {/* Production: the explicit confirmation of the sponsor decision (16 October 2026), traced in the
          audit log. Danger: the firewall can cut a production service if a port was misjudged. */}
      <Dialog
        open={confirmProduction}
        onClose={() => setConfirmProduction(false)}
        tone="danger"
        title={t('import.confirm.title', { n: production.length })}
        description={t('import.confirm.body', { servers: production.join(', ') })}
        confirmLabel={pendingLabel(t('settings.enrolment.dialog.confirmProduction'), enrol.isPending)}
        confirmDisabled={enrol.isPending}
        onConfirm={() => { setConfirmProduction(false); enrol.mutate(true); }}
      />
      <Dialog
        open={stepUp}
        onClose={() => setStepUp(false)}
        title={t('settings.enrolment.stepUp.title')}
        description={t('import.stepUp')}
        confirmLabel={t('settings.enrolment.stepUp.confirm')}
        onConfirm={() => window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent('/servers/import')}`)}
      />
      <Dialog
        open={quit}
        onClose={() => setQuit(false)}
        tone="danger"
        title={t('import.quitDialog.title')}
        description={t('import.quitDialog.body')}
        confirmLabel={t('import.quitDialog.confirm')}
        onConfirm={leave}
      />
    </>
  );
}
